import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CustomButton } from '../../components/common/CustomButton'
import { useAdminUser, usePermission } from '../../hooks/usePermission'
import {
  money,
  rebates,
  toCents,
  toDollars,
  type RebateConfig,
  type RebateDetail,
  type RebateEligibility,
} from '../../services/hostedEvents'
import { dateTime, errorMessage } from './helpers'
import {
  Badge,
  ErrorBanner,
  Modal,
  Notice,
  Spinner,
  StatusBadge,
  inputClass,
  labelClass,
} from './ui'

type Action = 'approve' | 'reject' | 'reopen' | 'pay' | 'void'

const ACTION_COPY: Record<Action, { title: string; confirm: string; help: string }> = {
  approve: {
    title: 'Approve rebate',
    confirm: 'Approve',
    help: 'Commits this amount against the program budget. It still has to be paid afterwards.',
  },
  reject: {
    title: 'Reject rebate',
    confirm: 'Reject',
    help: 'The vendor will not receive this rebate.',
  },
  reopen: {
    title: 'Return to pending',
    confirm: 'Reopen',
    help: 'Clears the decision, any recorded payout, and any void, and frees the budget it held.',
  },
  pay: {
    title: 'Record payout',
    confirm: 'Mark as paid',
    help: 'Barta does not send this money automatically. Send it first, then record how it went out.',
  },
  void: {
    title: 'Void rebate',
    confirm: 'Void',
    help: 'Use when an approved or paid rebate should not stand. If money already went out, recover it separately.',
  },
}

export function Checks({ eligibility }: { eligibility: RebateEligibility }) {
  return (
    <ul className="space-y-1.5">
      {eligibility.checks.map((check) => (
        <li key={check.key} className="flex items-start gap-2 text-sm">
          <span
            aria-hidden
            className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white ${check.passed ? 'bg-emerald-600' : 'bg-red-600'}`}
          >
            <svg
              className="h-2.5 w-2.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={4}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={check.passed ? 'M4.5 12.75l6 6 9-13.5' : 'M6 18L18 6M6 6l12 12'}
              />
            </svg>
          </span>
          <span className="text-gray-800 dark:text-gray-200">
            <span className="sr-only">{check.passed ? 'Passed: ' : 'Failed: '}</span>
            {check.label}
            <span className="block text-xs text-gray-500 dark:text-gray-400">{check.detail}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">{children}</dd>
    </div>
  )
}

export function RebateDetailModal({
  rebateId,
  config,
  onClose,
  onChanged,
  showEventLink,
}: {
  rebateId: string
  config: RebateConfig
  onClose: () => void
  onChanged: () => void
  showEventLink?: boolean
}) {
  const canReview = usePermission('event_rebates', 'review')
  const canPay = usePermission('event_rebates', 'pay')
  const canVoid = usePermission('event_rebates', 'void')
  const { userId } = useAdminUser()
  const [rebate, setRebate] = useState<RebateDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [action, setAction] = useState<Action | null>(null)
  const [reason, setReason] = useState('')
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState(config.payout_methods[0]?.id || '')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRebate(await rebates.detail(rebateId))
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }, [rebateId])

  useEffect(() => {
    load()
  }, [load])

  const begin = (next: Action) => {
    setAction(next)
    setReason('')
    setNote('')
    setReference('')
    setAmount(rebate ? toDollars(rebate.amount_cents) : '')
    setError(null)
  }

  const drifted =
    rebate?.current &&
    rebate.status === 'pending' &&
    !rebate.is_exception &&
    (rebate.current.eligible !== rebate.eligibility.eligible ||
      rebate.current.amount_cents !== rebate.computed_cents)

  const payoutMethod = config.payout_methods.find((candidate) => candidate.id === method)
  const amountCents = toCents(amount)
  const amountChanged =
    action === 'approve' && rebate !== null && amountCents !== rebate.amount_cents
  const selfPayBlocked =
    action === 'pay' && config.require_distinct_payer && rebate?.decided_by === userId
  const invalid =
    (action === 'approve' &&
      (amountCents === null ||
        amountCents <= 0 ||
        amountCents > config.max_rebate_cents ||
        ((amountChanged || drifted) && !reason.trim()))) ||
    ((action === 'reject' || action === 'reopen' || action === 'void') && !reason.trim()) ||
    (action === 'pay' &&
      (!payoutMethod || (payoutMethod.reference_required && !reference.trim()) || selfPayBlocked))

  const submit = async () => {
    if (!rebate || !action || invalid) return
    setBusy(true)
    setError(null)
    try {
      const trimmed = reason.trim() || null
      if (action === 'approve')
        await rebates.approve(
          rebate.id,
          rebate.revision,
          amountChanged || drifted ? amountCents : null,
          trimmed
        )
      else if (action === 'pay')
        await rebates.pay(rebate.id, rebate.revision, {
          method,
          reference: reference.trim() || null,
          note: note.trim() || null,
        })
      else await rebates[action](rebate.id, rebate.revision, reason.trim())
      setAction(null)
      onChanged()
      await load()
    } catch (caught) {
      setAction(null)
      await load()
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  const available: Action[] = !rebate
    ? []
    : ([
        canReview && rebate.status === 'pending' && 'approve',
        canReview && (rebate.status === 'pending' || rebate.status === 'approved') && 'reject',
        canPay && rebate.status === 'approved' && 'pay',
        canReview &&
          (rebate.status === 'rejected' ||
            rebate.status === 'approved' ||
            rebate.status === 'void') &&
          'reopen',
        canVoid && (rebate.status === 'approved' || rebate.status === 'paid') && 'void',
      ].filter(Boolean) as Action[])

  return (
    <Modal
      title={rebate ? `Rebate for ${rebate.shop_name || 'vendor'}` : 'Rebate'}
      onClose={onClose}
      wide
    >
      {error && <ErrorBanner message={error} />}
      {!rebate ? (
        error ? null : (
          <Spinner />
        )
      ) : action ? (
        <div>
          <h4 className="font-heading text-lg font-semibold text-gray-900 dark:text-white">
            {ACTION_COPY[action].title}
          </h4>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {ACTION_COPY[action].help}
          </p>

          {action === 'approve' && (
            <div className="mt-4">
              <label className={labelClass} htmlFor="approve-amount">
                Amount ($)
              </label>
              <input
                id="approve-amount"
                className={inputClass}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The rules computed {money(rebate.computed_cents)}. Changing it needs a reason. Limit{' '}
                {money(config.max_rebate_cents)}.
              </p>
              {rebate.program_status !== 'active' && (
                <div className="mt-3">
                  <Notice tone="amber">
                    The program is {rebate.program_status}. Approval will be refused until it is
                    active.
                  </Notice>
                </div>
              )}
            </div>
          )}

          {action === 'pay' && (
            <div className="mt-4 space-y-3">
              <Notice>
                Paying {money(rebate.amount_cents)} to {rebate.shop_name || 'this vendor'}.
              </Notice>
              {selfPayBlocked && (
                <Notice tone="amber">
                  You approved this rebate, so a different admin has to record the payout.
                </Notice>
              )}
              <div>
                <label className={labelClass} htmlFor="pay-method">
                  How it was sent
                </label>
                <select
                  id="pay-method"
                  className={inputClass}
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                >
                  {config.payout_methods.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass} htmlFor="pay-reference">
                  Reference{payoutMethod?.reference_required ? '' : ' (optional)'}
                </label>
                <input
                  id="pay-reference"
                  className={inputClass}
                  maxLength={200}
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  placeholder="Transfer ID, check number, or transaction link"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="pay-note">
                  Note (optional)
                </label>
                <input
                  id="pay-note"
                  className={inputClass}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </div>
            </div>
          )}

          {action !== 'pay' && (
            <div className="mt-4">
              <label className={labelClass} htmlFor="action-reason">
                Reason{action === 'approve' && !amountChanged && !drifted ? ' (optional)' : ''}
              </label>
              <textarea
                id="action-reason"
                className={inputClass}
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <CustomButton variant="secondary" onClick={() => setAction(null)} disabled={busy}>
              Back
            </CustomButton>
            <CustomButton
              variant={
                action === 'reject' || action === 'void'
                  ? 'danger'
                  : action === 'reopen'
                    ? 'primary'
                    : 'success'
              }
              onClick={submit}
              loading={busy}
              disabled={!!invalid}
            >
              {ACTION_COPY[action].confirm}
            </CustomButton>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={rebate.status} />
            {rebate.is_exception && <Badge tone="purple">Exception</Badge>}
            <span className="font-heading text-2xl font-bold text-gray-900 dark:text-white">
              {money(rebate.amount_cents)}
            </span>
            {rebate.amount_cents !== rebate.computed_cents && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                rules computed {money(rebate.computed_cents)}
              </span>
            )}
          </div>

          {drifted && (
            <Notice tone="amber">
              This vendor's numbers changed since the rebate was opened. Under the rules today they
              are{' '}
              {rebate.current?.eligible
                ? `eligible for ${money(rebate.current.amount_cents)}`
                : 'not eligible'}
              . Refresh pending rebates from the event's Vendors tab, or approve anyway by giving a
              reason.
            </Notice>
          )}

          <dl className="grid gap-4 sm:grid-cols-3">
            <Field label="Vendor">
              {rebate.shop_name || 'Unknown'}
              {rebate.shop_handle && (
                <span className="block text-xs text-gray-500">@{rebate.shop_handle}</span>
              )}
            </Field>
            <Field label="Event">
              {showEventLink ? (
                <Link
                  className="text-[#7f4f9a] underline dark:text-[#b58fcc]"
                  to={`/hosted-events/${rebate.market_id}`}
                  onClick={onClose}
                >
                  {rebate.market_name || 'Open event'}
                </Link>
              ) : (
                rebate.market_name || 'Unknown'
              )}
            </Field>
            <Field label="Opened">{dateTime(rebate.created_at)}</Field>
            {rebate.signals && (
              <>
                <Field label="Booth fee">
                  {money(rebate.signals.booth_price_cents)}
                  <span className="block text-xs text-gray-500">
                    {rebate.signals.payment_method === 'offline'
                      ? 'Paid offline'
                      : `${money(rebate.signals.total_paid_cents)} charged`}
                    {rebate.signals.refunded_cents > 0 &&
                      `, ${money(rebate.signals.refunded_cents)} refunded`}
                  </span>
                </Field>
                <Field label="Check-in">
                  {rebate.signals.checked_in_at
                    ? dateTime(rebate.signals.checked_in_at)
                    : rebate.signals.no_show_at
                      ? 'No-show'
                      : 'Not recorded'}
                </Field>
                <Field label="In-person sales">
                  {rebate.signals.sales_count} · {money(rebate.signals.sales_cents)}
                </Field>
              </>
            )}
            {rebate.decided_at && <Field label="Decided">{dateTime(rebate.decided_at)}</Field>}
            {rebate.decision_reason && (
              <Field label="Decision reason">{rebate.decision_reason}</Field>
            )}
            {rebate.paid_at && (
              <Field label="Paid">
                {dateTime(rebate.paid_at)}
                <span className="block text-xs text-gray-500">
                  {config.payout_methods.find((option) => option.id === rebate.payout_method)
                    ?.label || rebate.payout_method}
                  {rebate.payout_reference && ` · ${rebate.payout_reference}`}
                </span>
              </Field>
            )}
            {rebate.payout_note && <Field label="Payout note">{rebate.payout_note}</Field>}
            {rebate.void_reason && <Field label="Void reason">{rebate.void_reason}</Field>}
          </dl>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
              Rules when opened
            </h4>
            <Checks eligibility={rebate.eligibility} />
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">History</h4>
            <ol className="space-y-2 border-l border-gray-200 pl-4 dark:border-[#3d2c4f]">
              {rebate.history.map((entry) => (
                <li key={entry.id} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium capitalize">{entry.action.replace(/_/g, ' ')}</span>
                  {entry.from_status && entry.from_status !== entry.to_status && (
                    <span className="text-gray-500">
                      {' '}
                      · {entry.from_status} to {entry.to_status}
                    </span>
                  )}
                  <span className="text-gray-500"> · {money(entry.amount_cents)}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {dateTime(entry.created_at)} by{' '}
                    {entry.actor_id === userId
                      ? 'you'
                      : entry.actor_email || entry.actor_id.slice(0, 8)}
                    {typeof entry.detail.reason === 'string' && ` · ${entry.detail.reason}`}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {available.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4 dark:border-[#2d2236]">
              {available.map((next) => (
                <CustomButton
                  key={next}
                  size="sm"
                  variant={
                    next === 'approve' || next === 'pay'
                      ? 'success'
                      : next === 'reopen'
                        ? 'secondary'
                        : 'danger'
                  }
                  onClick={() => begin(next)}
                >
                  {ACTION_COPY[next].title}
                </CustomButton>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
